import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import type {PropertyDefinitionBase, SourceType} from '@github-ui/custom-properties-types'
import {human} from '@github-ui/formatters'
import {hpcAttrs} from '@github-ui/hpc'
import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {ListItemTrailingBadge} from '@github-ui/list-view/ListItemTrailingBadge'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {enterprisePath, ownerPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-router'
import {type Icon, LockIcon, PlugIcon, ShieldLockIcon} from '@primer/octicons-react'
import {Link as PrimerLink, Truncate} from '@primer/react'
import {clsx} from 'clsx'
import {memo, type ReactNode, useEffect} from 'react'

import {getDefinitionLabel} from '../helpers/definition-type-label'
import styles from './DefinitionsList.module.css'
import {ListEmptyState} from './ListEmptyState'

const debounceAnnounce = debounce(announce, 300)

interface Props<T extends PropertyDefinitionBase> {
  totalCount: number
  definitions: T[]
  renderItem(definition: T): ReactNode
  /** If passed in, this function will be used instead of the default "No properties have been found" message. */
  renderEmptyState?(): ReactNode
}
export function DefinitionsList<T extends PropertyDefinitionBase>({
  totalCount,
  definitions,
  renderItem,
  renderEmptyState,
}: Props<T>) {
  const countLabel = `${human(totalCount, {suffix: false})} ${totalCount === 1 ? 'property' : 'properties'}`

  useEffect(() => {
    debounceAnnounce(`${countLabel} found.`)
  }, [definitions, countLabel])

  if (!definitions.length) {
    return renderEmptyState ? (
      renderEmptyState()
    ) : (
      <ListEmptyState>
        <strong>No properties have been found</strong>
      </ListEmptyState>
    )
  }

  return (
    <div {...hpcAttrs()} data-testid="repos-definitions-list" className={styles.definitionsListContainer}>
      <ListView
        title="Property definitions"
        metadata={
          <ListViewMetadata
            className="rounded-top-2"
            title={<span className={styles.propertiesCountText}>{countLabel}</span>}
          />
        }
      >
        {definitions.map(renderItem)}
      </ListView>
    </div>
  )
}

function DefinitionDisplayName({definition, linkTo}: {definition: PropertyDefinitionBase; linkTo: string}) {
  const isReadOnly = definition.type === 'external'

  return (
    <ListItemTitle
      value={getControlLabel(definition)}
      href={isReadOnly ? undefined : linkTo}
      linkProps={
        isReadOnly
          ? undefined
          : {
              as: Link,
              to: linkTo,
            }
      }
      containerClassName={styles.ListItemTitle}
      headingClassName={clsx(styles.ListItemHeading, isReadOnly && styles.ReadOnlyListItemHeading)}
      trailingBadges={
        isReadOnly
          ? [
              <ListItemTrailingBadge key="read-only" title="Read-only">
                <LockIcon aria-label="Read-only" className="color-fg-muted" size="small" />
              </ListItemTrailingBadge>,
            ]
          : undefined
      }
    />
  )
}

interface DefinitionListItemProps {
  definition: PropertyDefinitionBase
  /**
   * Settings level defines if a managed by label is shown for custom properties.
   * org - label is shown for business properties only
   * business - label is shown for both org and business properties
   * undefined - managed by label is omitted
   * External properties show their owner whenever one is available.
   */
  settingsLevel?: SourceType
  linkTo: string
}

const DefinitionListItem = memo((props: DefinitionListItemProps) => {
  const {definition, linkTo} = props
  return (
    <ListItem
      className={styles.listItem}
      title={<DefinitionDisplayName definition={definition} linkTo={linkTo} />}
      metadata={<DefinitionMetadata {...props} />}
      metadataContainerClassName={styles.metadataContainer}
    >
      <ListItemMainContent>
        {definition.description && (
          <ListItemDescription className={styles.ListItemDescription}>
            <ListItemDescriptionItem className={styles.ListItemDescriptionItem}>
              {definition.description}
            </ListItemDescriptionItem>
          </ListItemDescription>
        )}
      </ListItemMainContent>
    </ListItem>
  )
})
DefinitionListItem.displayName = 'DefinitionListItem'

function DefinitionMetadata(props: DefinitionListItemProps) {
  const {definition} = props
  return (
    <>
      <ListItemMetadata>
        <span>{getDefinitionLabel(definition.valueType)}</span>
      </ListItemMetadata>
      <ListItemMetadata className={styles.listItemMetadata}>
        <ManagedByLabel {...props} />
      </ListItemMetadata>
    </>
  )
}

interface ManagedByConfig {
  Icon?: Icon
  url: string
  displayName: string
}

function getManagedByConfig(
  definition: PropertyDefinitionBase,
  settingsLevel?: SourceType,
): ManagedByConfig | undefined {
  const {owner, source, type} = definition

  // External properties always render the managed by label when an owner is available.
  if (type === 'external') {
    if (!owner) return undefined

    return {
      Icon: PlugIcon,
      url: owner.url,
      displayName: owner.displayName,
    }
  }

  // Organization settings only show the managed by label for enterprise owned properties.
  // Enterprise settings show the managed by label for both organization and enterprise owned properties.
  if (!settingsLevel || (source.type === 'org' && settingsLevel === 'org')) return undefined

  return {
    Icon: settingsLevel === 'org' ? ShieldLockIcon : undefined,
    url: source.type === 'business' ? enterprisePath({slug: source.slug}) : ownerPath({owner: source.slug}),
    displayName: source.name,
  }
}

function ManagedByLabel({definition, settingsLevel}: DefinitionListItemProps) {
  const managedByConfig = getManagedByConfig(definition, settingsLevel)
  if (!managedByConfig) return null

  const {Icon, url, displayName} = managedByConfig
  return (
    <>
      <span className="mx-2">•</span>
      <div data-testid={`${definition.propertyName}-managed-by-label`} className={styles.managedByLabelContainer}>
        <div className="d-flex flex-row flex-items-center gap-1">
          {Icon && (
            <Icon className="color-fg-muted" size="small" data-testid={`${definition.propertyName}-managed-by-icon`} />
          )}
          <span className="color-fg-muted">Managed by </span>
          <PrimerLink inline href={url}>
            <Truncate title={displayName}>{displayName}</Truncate>
          </PrimerLink>
        </div>
      </div>
    </>
  )
}

DefinitionsList.Item = DefinitionListItem

function getControlLabel({propertyName, required}: PropertyDefinitionBase) {
  return `${propertyName}${required ? ' *' : ''}`
}
